import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { config, isConfigured } from './config';

/**
 * Auth design (D-G in ISA): Cognito User Pool, email + password,
 * USER_SRP_AUTH flow only. ID token attached to API requests via
 * `Authorization: Bearer`.
 *
 * Token storage: sessionStorage. Choosing sessionStorage over localStorage
 * trades cross-tab persistence for shorter blast-radius on XSS — the token
 * dies when the tab closes. CSP headers on the CloudFront distribution
 * (admin-stack.ts) make XSS hard in the first place; sessionStorage is the
 * defence-in-depth layer.
 */

interface AuthState {
  readonly status: 'loading' | 'signed-out' | 'signed-in' | 'force-change-password';
  readonly email: string | null;
  readonly idToken: string | null;
  readonly error: string | null;
}

interface AuthContextValue extends AuthState {
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly completeNewPassword: (newPassword: string) => Promise<void>;
  readonly signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_KEY = 'feed-admin-session';

interface PersistedSession {
  readonly email: string;
  readonly idToken: string;
  readonly expiresAt: number; // ms epoch
}

function userPool(): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
  });
}

function loadSession(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'email' in parsed &&
      'idToken' in parsed &&
      'expiresAt' in parsed
    ) {
      const s = parsed as PersistedSession;
      // 60s skew so we don't hand out a token that's about to expire.
      if (s.expiresAt > Date.now() + 60_000) {
        return s;
      }
    }
  } catch {
    // Corrupted session — drop it.
  }
  return null;
}

function saveSession(s: PersistedSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

interface PendingPasswordChange {
  readonly cognitoUser: CognitoUser;
  readonly userAttributes: Record<string, string>;
}

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    email: null,
    idToken: null,
    error: null,
  });

  const [pending, setPending] = useState<PendingPasswordChange | null>(null);

  // Boot: hydrate from sessionStorage if a valid session is parked there.
  useEffect(() => {
    if (!isConfigured()) {
      setState({
        status: 'signed-out',
        email: null,
        idToken: null,
        error: 'Frontend was built without VITE_USER_POOL_ID / VITE_USER_POOL_CLIENT_ID / VITE_API_ENDPOINT. Re-run the deploy.',
      });
      return;
    }
    const persisted = loadSession();
    if (persisted !== null) {
      setState({
        status: 'signed-in',
        email: persisted.email,
        idToken: persisted.idToken,
        error: null,
      });
    } else {
      setState({ status: 'signed-out', email: null, idToken: null, error: null });
    }
  }, []);

  // Listen for `session_expired` events from api.ts when the API gateway
  // returns 401 — typically because the 1h Cognito ID token has elapsed
  // mid-browse. Sign out cleanly so the next render redirects to /login.
  useEffect(() => {
    function onSessionExpired(): void {
      clearSession();
      setPending(null);
      setState({
        status: 'signed-out',
        email: null,
        idToken: null,
        error: 'Session expired — sign in again.',
      });
    }
    window.addEventListener('feed-admin:session-expired', onSessionExpired);
    return () => { window.removeEventListener('feed-admin:session-expired', onSessionExpired); };
  }, []);

  const signIn = useCallback(
    (email: string, password: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool() });
        const authDetails = new AuthenticationDetails({ Username: email, Password: password });

        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session: CognitoUserSession) => {
            const idToken = session.getIdToken().getJwtToken();
            const expiresAt = session.getIdToken().getExpiration() * 1000;
            saveSession({ email, idToken, expiresAt });
            setState({ status: 'signed-in', email, idToken, error: null });
            resolve();
          },
          onFailure: (err: Error) => {
            setState({
              status: 'signed-out',
              email: null,
              idToken: null,
              error: err.message ?? 'sign-in failed',
            });
            reject(err);
          },
          newPasswordRequired: (userAttributes: Record<string, string>) => {
            // Strip immutable attributes (Cognito rejects email + email_verified
            // in completeNewPasswordChallenge).
            const { email: _email, email_verified: _v, ...required } = userAttributes;
            setPending({ cognitoUser, userAttributes: required });
            setState({
              status: 'force-change-password',
              email,
              idToken: null,
              error: 'Set a new permanent password to continue.',
            });
            resolve();
          },
          mfaRequired: () => {
            setState({
              status: 'signed-out',
              email: null,
              idToken: null,
              error: 'MFA challenge — not yet supported in this UI. Disable MFA or wait for P6.6.',
            });
            reject(new Error('MFA not supported'));
          },
        });
      }),
    [],
  );

  const completeNewPassword = useCallback(
    (newPassword: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (pending === null) {
          reject(new Error('no pending password change'));
          return;
        }
        pending.cognitoUser.completeNewPasswordChallenge(newPassword, pending.userAttributes, {
          onSuccess: (session: CognitoUserSession) => {
            const email = pending.cognitoUser.getUsername();
            const idToken = session.getIdToken().getJwtToken();
            const expiresAt = session.getIdToken().getExpiration() * 1000;
            saveSession({ email, idToken, expiresAt });
            setState({ status: 'signed-in', email, idToken, error: null });
            setPending(null);
            resolve();
          },
          onFailure: (err: Error) => {
            setState((s) => ({ ...s, error: err.message ?? 'password change failed' }));
            reject(err);
          },
        });
      }),
    [pending],
  );

  const signOut = useCallback(() => {
    clearSession();
    setPending(null);
    setState({ status: 'signed-out', email: null, idToken: null, error: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, completeNewPassword, signOut }),
    [state, signIn, completeNewPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
